/* eslint-disable react/no-array-index-key */
/* eslint-disable max-lines */
/* eslint-disable max-len */
import {
    Box,
    Button,
    Checkbox,
    Divider,
    Flex,
    Grid,
    GridItem,
    HStack,
    ModalBody,
    ModalFooter,
    Skeleton,
    Text,
    VStack,
} from '@chakra-ui/react';
import { Occupant } from '@acp/shared';
import { Alert, SkeletonMotion } from '@acp/core-ui';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { SubmitBookingFormSchemaValues } from './form-scheme';
import { PropertyAddressCard } from '../../../property/components/property-address-card';
import { ConfirmBookingCard } from './confirm-booking-card';
import { AddNominatedContact } from './add-nominated-contact';
import { OccupantCard } from './occupant-card';
import { useTenancyDetails } from '../../../tenancy';

function ContactCardsSkeleton() {
    return (
        <VStack alignItems="start" opacity={0.4} w="full" gap={4}>
            <SkeletonMotion height={8} width="50%" sx={{ animationDuration: '2s' }} />
            <Skeleton height={28} width="100%" sx={{ animationDuration: '2.4s' }} />
            <SkeletonMotion height={8} width="30%" sx={{ animationDuration: '2s' }} />
        </VStack>
    );
}

function NoContactAvailable() {
    return (
        <Flex
            flexDir="column"
            alignItems="center"
            justifyContent="center"
            py={8}
            px={8}
            borderColor="muted.50"
            borderWidth={1}
            rounded="md"
        >
            <Text color="red.500" fontWeight="bold">No contact found</Text>
            <Text fontSize="sm" color="black">There is no contact available, please try again or contact us for support</Text>
        </Flex>
    );
}

export function ChooseBookingContactsScreen({
    propertyId,
    onSubmit,
    onBack,
}: {
    propertyId: number;
    onSubmit: (vals: SubmitBookingFormSchemaValues) => void;
    onBack: () => void;
}) {
    const tenancyDetails = useTenancyDetails(propertyId);
    const occupants = tenancyDetails.data?.currentTenancy.occupants;

    const {
        control, formState, handleSubmit, setValue, watch,
    } = useFormContext<SubmitBookingFormSchemaValues>();
    const {
        fields, append, remove, update,
    } = useFieldArray({
        control,
        name: 'oasBooking.contacts',
    });

    const { contacts } = watch('oasBooking');

    // set the first occupant as the primary contact by default
    useEffect(() => {
        if (occupants && occupants.length > 0 && fields.length === 0) {
            const mobileNumber = occupants[0].mobile !== undefined ? occupants[0].mobile : occupants[0].mobileContacts[0];

            setValue('oasBooking.contacts', [{
                name: occupants[0].name,
                email: occupants[0].email ?? '',
                mobile: mobileNumber,
                isPrimaryContact: true,
            }]);
        }
    }, [occupants]);

    const handleSelectOccupant = (
        occupant: Occupant,
        isChecked: boolean
    ) => {
        const email = occupant.email ?? '';
        const isOccupantExists = fields.some((field) => field.email === email);
        const mobileNumber = occupant.mobile !== undefined ? occupant.mobile : occupant.mobileContacts[0];

        if (isChecked && !isOccupantExists) {
            // add contact
            append({
                name: occupant.name,
                email,
                mobile: mobileNumber,
                isPrimaryContact: false,
            });
        } else if (!isChecked && isOccupantExists) {
            // remove contact
            const index = fields.findIndex((field) => field.email === email);
            if (index !== -1) {
                remove(index);
            }
        }
    };

    const handleSelectAllOccupants = (isChecked: boolean) => {
        if (isChecked) {
            occupants?.forEach((occupant) => {
                const email = occupant.email ?? '';
                const isOccupantExists = fields.some((field) => field.email === email);
                const mobileNumber = occupant.mobile !== undefined ? occupant.mobile : occupant.mobileContacts[0];

                if (!isOccupantExists) {
                    append({
                        name: occupant.name,
                        email,
                        mobile: mobileNumber,
                        isPrimaryContact: false,
                    });
                }
            });
        } else {
            setValue('oasBooking.contacts', []);
        }
    };

    const handleSetPrimaryContact = (occupant: Occupant) => {
        const email = occupant.email ?? '';
        const isOccupantExists = fields.some((field) => field.email === email);
        const isAlreadyPrimary = fields.some((field) => field.email === email && field.isPrimaryContact);

        fields.forEach((field, i) => {
            const { id, ...rest } = field; // Exclude id
            update(i, { ...rest, isPrimaryContact: false });
        });

        if (isOccupantExists) {
            // If occupant exists, update its isPrimaryContact to true
            fields.forEach((field, i) => {
                if (field.email === email) {
                    const { id, ...rest } = field; // Exclude id
                    update(i, { ...rest, isPrimaryContact: !isAlreadyPrimary });
                }
            });
        } else {
            // If occupant does not exist, append the new occupant with isPrimaryContact set to true
            const mobileNumber = occupant.mobile !== undefined ? occupant.mobile : occupant.mobileContacts[0];

            append({
                name: occupant.name,
                email,
                mobile: mobileNumber,
                isPrimaryContact: true,
            });
        }
    };

    return (
        <>
            <ModalBody>
                <PropertyAddressCard w="full" mb={5} propertyId={propertyId}>
                    <ConfirmBookingCard />
                </PropertyAddressCard>
                <Text fontSize="md" mb={4}>
                    This booking will be made on behalf of the tenant. An SMS and email
                    confirmation will be sent to the selected recipients once confirmed.
                </Text>

                <Divider my={3} />

                {tenancyDetails.isLoading
                    ? <ContactCardsSkeleton />
                    : (
                        <Box>
                            {occupants?.length === 0
                                ? <NoContactAvailable />
                                : (
                                    <Box>
                                        <HStack justifyContent="space-between" alignItems="flex-start" mb={4}>
                                            <Text fontSize="lg" fontWeight={500}>
                                                Choose recipients
                                            </Text>

                                            <Checkbox
                                                isChecked={contacts.length === occupants?.length}
                                                onChange={(e) => {
                                                    handleSelectAllOccupants(e.target.checked);
                                                }}
                                            >
                                                <Text fontSize="sm">
                                                    Select all
                                                </Text>
                                            </Checkbox>
                                        </HStack>

                                        <HStack w="full" align="left">
                                            <Grid
                                                templateColumns={{ base: 'auto', md: 'repeat(2, 1fr)' }}
                                                gap={3}
                                                h="auto"
                                                w={{ base: 'full', md: 'auto' }}
                                            >
                                                {occupants?.map((occupant, index) => {
                                                    const email = occupant.email ?? '';
                                                    const isPrimaryContact = fields.find((f) => f.email === email)?.isPrimaryContact ?? false;
                                                    const isExistingContact = fields.some((f) => f.email === email);

                                                    return (
                                                        <GridItem w="100%" key={index}>
                                                            <OccupantCard
                                                                occupant={occupant}
                                                                isExistingContact={isExistingContact}
                                                                isPrimaryContact={isPrimaryContact}
                                                                onSelect={handleSelectOccupant}
                                                                onSetPrimaryContact={handleSetPrimaryContact}
                                                            />
                                                        </GridItem>
                                                    );
                                                })}
                                            </Grid>
                                        </HStack>

                                        <Divider my={3} />
                                        <AddNominatedContact />
                                    </Box>
                                )}
                        </Box>
                    )}

                {/* Errors */}
                {Object.keys(formState.errors.oasBooking?.contacts ?? {}).length > 0 && (
                    <Alert title={formState.errors.oasBooking?.contacts?.message!} status="error" mt={5} />
                )}
            </ModalBody>

            <ModalFooter justifyContent="space-between">
                <Button size="md" colorScheme="muted" variant="ghost" onClick={onBack}>
                    Back
                </Button>
                <Button
                    colorScheme="secondary"
                    variant="solid"
                    size="md"
                    isDisabled={!!(occupants?.length === 0)}
                    onClick={handleSubmit(onSubmit)}
                >
                    Next
                </Button>
            </ModalFooter>
        </>
    );
}
